import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Envelope, Phone } from '@phosphor-icons/react';
import axios from 'axios';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [activeForm, setActiveForm] = useState('contact');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [quoteData, setQuoteData] = useState({
    name: '',
    email: '',
    phone: '',
    project_type: '',
    materials_needed: '',
    project_description: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(`${API_URL}/api/contact`, {
        ...contactData,
        language: i18n.language
      });
      
      setMessage({ type: 'success', text: t('messageSent') });
      setContactData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setMessage({ type: 'error', text: t('tryAgain') });
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(`${API_URL}/api/quote`, {
        ...quoteData,
        language: i18n.language
      });
      
      setMessage({ type: 'success', text: t('quoteSent') });
      setQuoteData({ name: '', email: '', phone: '', project_type: '', materials_needed: '', project_description: '' });
    } catch (error) {
      setMessage({ type: 'error', text: t('tryAgain') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      <section className="section-padding bg-[#F5F5F0]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl text-center mx-auto">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">
              {t('contact')}
            </p>
            <h1 className={`text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-none mb-6 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`} data-testid="contact-title">
              {t('contactTitle')}
            </h1>
            <p className={`text-xl text-gray-700 ${
              isRTL ? 'font-arabic' : ''
            }`}>
              {t('contactSubtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Form Section */}
            <div className="lg:col-span-7">
              {/* Form Toggle */}
              <div className="flex border-b border-black/10 mb-8">
                <button
                  onClick={() => setActiveForm('contact')}
                  data-testid="contact-tab"
                  className={`px-6 py-3 text-sm font-medium transition-all ${
                    activeForm === 'contact'
                      ? 'border-b-2 border-[#C4A661] text-[#C4A661]'
                      : 'text-gray-500 hover:text-black'
                  }`}
                >
                  {t('contact')}
                </button>
                <button
                  onClick={() => setActiveForm('quote')}
                  data-testid="quote-tab"
                  className={`px-6 py-3 text-sm font-medium transition-all ${
                    activeForm === 'quote'
                      ? 'border-b-2 border-[#C4A661] text-[#C4A661]'
                      : 'text-gray-500 hover:text-black'
                  }`}
                >
                  {t('quoteTitle')}
                </button>
              </div>

              {message && (
                <div
                  className={`mb-6 p-4 ${
                    message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                  data-testid="form-message"
                >
                  {message.text}
                </div>
              )}

              {/* Contact Form */}
              {activeForm === 'contact' && (
                <form onSubmit={handleContactSubmit} data-testid="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('yourName')}
                      </label>
                      <input
                        type="text"
                        required
                        value={contactData.name}
                        onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                        data-testid="contact-name"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('yourEmail')}
                      </label>
                      <input
                        type="email"
                        required
                        value={contactData.email}
                        onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                        data-testid="contact-email"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('yourPhone')}
                      </label>
                      <input
                        type="tel"
                        required
                        value={contactData.phone}
                        onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                        data-testid="contact-phone"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('yourMessage')}
                      </label>
                      <textarea
                        required
                        rows="5"
                        value={contactData.message}
                        onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                        data-testid="contact-message"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors resize-none"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit"
                      className="w-full px-8 py-4 bg-[#0F0F0F] text-white text-sm font-medium hover:bg-[#C4A661] transition-all duration-300 disabled:opacity-50"
                    >
                      {loading ? t('sending') : t('sendMessage')}
                    </button>
                  </div>
                </form>
              )}

              {/* Quote Form */}
              {activeForm === 'quote' && (
                <form onSubmit={handleQuoteSubmit} data-testid="quote-form">
                  <div className="space-y-6">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('yourName')}
                      </label>
                      <input
                        type="text"
                        required
                        value={quoteData.name}
                        onChange={(e) => setQuoteData({ ...quoteData, name: e.target.value })}
                        data-testid="quote-name"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('yourEmail')}
                      </label>
                      <input
                        type="email"
                        required
                        value={quoteData.email}
                        onChange={(e) => setQuoteData({ ...quoteData, email: e.target.value })}
                        data-testid="quote-email"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('yourPhone')}
                      </label>
                      <input
                        type="tel"
                        required
                        value={quoteData.phone}
                        onChange={(e) => setQuoteData({ ...quoteData, phone: e.target.value })}
                        data-testid="quote-phone"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('projectType')}
                      </label>
                      <input
                        type="text"
                        required
                        value={quoteData.project_type}
                        onChange={(e) => setQuoteData({ ...quoteData, project_type: e.target.value })}
                        data-testid="quote-project-type"
                        placeholder={isRTL ? 'مثال: فيلا سكنية، فندق، مكتب' : 'e.g., Residential Villa, Hotel, Office'}
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('materialsNeeded')}
                      </label>
                      <input
                        type="text"
                        required
                        value={quoteData.materials_needed}
                        onChange={(e) => setQuoteData({ ...quoteData, materials_needed: e.target.value })}
                        data-testid="quote-materials"
                        placeholder={isRTL ? 'مثال: أرضيات رخامية، كسوة جدران' : 'e.g., Marble Flooring, Wall Cladding'}
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isRTL ? 'font-arabic' : ''}`}>
                        {t('projectDescription')}
                      </label>
                      <textarea
                        required
                        rows="5"
                        value={quoteData.project_description}
                        onChange={(e) => setQuoteData({ ...quoteData, project_description: e.target.value })}
                        data-testid="quote-description"
                        className="w-full px-4 py-3 border border-black/20 focus:border-[#C4A661] focus:outline-none transition-colors resize-none"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="quote-submit"
                      className="w-full px-8 py-4 bg-[#0F0F0F] text-white text-sm font-medium hover:bg-[#C4A661] transition-all duration-300 disabled:opacity-50"
                    >
                      {loading ? t('sending') : t('submitQuote')}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5">
              <div className="bg-[#F5F5F0] p-8 space-y-8">
                <div className="flex items-start space-x-4">
                  <MapPin size={28} weight="light" className="text-[#C4A661] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className={`text-lg font-medium mb-2 ${isRTL ? 'font-arabic' : 'font-heading'}`}>
                      {t('location')}
                    </h3>
                    <p className={`text-sm text-gray-600 ${isRTL ? 'font-arabic' : ''}`}>
                      {t('locationText')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Envelope size={28} weight="light" className="text-[#C4A661] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className={`text-lg font-medium mb-2 ${isRTL ? 'font-arabic' : 'font-heading'}`}>
                      {t('emailLabel')}
                    </h3>
                    <a href="mailto:operations@dsmarbles.ae" className="text-sm text-gray-600 hover:text-[#C4A661]">
                      operations@dsmarbles.ae
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Phone size={28} weight="light" className="text-[#C4A661] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className={`text-lg font-medium mb-2 ${isRTL ? 'font-arabic' : 'font-heading'}`}>
                      {t('phoneLabel')}
                    </h3>
                    <a href="tel:+97168048154" className="text-sm text-gray-600 hover:text-[#C4A661]">
                      +971 6 804 8154
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
